import type { Types } from "@cornerstonejs/tools";

export interface SegmentationGeometryTarget {
  segmentationId: string;
  segmentIndex: number;
  viewPlaneNormal: [number, number, number];
}

type CheckpointContour = Types.Annotation &
  Pick<Types.ContourSegmentationAnnotation, "onInterpolationComplete">;

export interface SegmentationGeometryCheckpoint {
  target: SegmentationGeometryTarget;
  contours: CheckpointContour[];
}

/**
 * Lasso annotations carry a Cornerstone interpolation callback. Keep that
 * behavior by reference, while deeply copying the mutable annotation data.
 * Unknown uncopyable fields still throw rather than silently losing geometry.
 */
export function copyContourForCheckpoint(annotation: CheckpointContour): CheckpointContour {
  const { onInterpolationComplete, ...data } = annotation;
  const copy: CheckpointContour = structuredClone(data);
  if (Object.prototype.hasOwnProperty.call(annotation, "onInterpolationComplete")) {
    copy.onInterpolationComplete = onInterpolationComplete;
  }
  return copy;
}

/**
 * Select the same segment and plane that nnInteractive will replace.
 * The caller supplies annotations from the target viewport's frame.
 */
export function matchesGeometryTarget(
  annotation: Types.Annotation,
  target: SegmentationGeometryTarget,
): boolean {
  const segmentation = annotation.data?.segmentation as
    | { segmentationId?: string; segmentIndex?: number }
    | undefined;
  const normal = annotation.metadata?.viewPlaneNormal;

  if (
    annotation.metadata?.toolName !== "PlanarFreehandContourSegmentationTool" ||
    segmentation?.segmentationId !== target.segmentationId ||
    segmentation?.segmentIndex !== target.segmentIndex ||
    !normal ||
    normal.length !== 3
  ) {
    return false;
  }

  const targetNormal = target.viewPlaneNormal;
  return (
    Math.abs(
      normal[0] * targetNormal[0] + normal[1] * targetNormal[1] + normal[2] * targetNormal[2],
    ) > 0.95
  );
}

/**
 * Copy live geometry before replacement.
 * An empty contour list is a valid blank starting state.
 * Throws if the geometry cannot be copied.
 */
export function captureSegmentationGeometry(
  annotations: readonly Types.Annotation[],
  target: SegmentationGeometryTarget,
): SegmentationGeometryCheckpoint {
  const contours = annotations.filter((annotation) => matchesGeometryTarget(annotation, target));

  return {
    target: structuredClone(target),
    contours: contours.map(copyContourForCheckpoint),
  };
}
